import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
// PERBAIKAN: Import tipe FieldValues yang benar dari react-hook-form
import { FieldValues } from "react-hook-form";
import { privateApi } from "../../../api/api";
import { toast } from "sonner";
import { CreateBookingFormV4 } from "../schema/CreateBookingSchemaV4";

// Tipe untuk state yang melacak kamar yang sudah dibuat
export interface CreatedRoom {
    index: number;
    bookingId: string;
    reservationId: string;
}

// Tipe untuk opsi yang dikirim ke fungsi mutate
interface MutateOptions {
    createdRooms: CreatedRoom[];
    // PERBAIKAN: Gunakan tipe Readonly<FieldValues> yang aman dan benar
    dirtyFields: Readonly<FieldValues>;
}

// Tipe untuk callbacks
interface GroupBookingCallbacks {
    onRoomProcessStart: (index: number) => void;
    onRoomProcessSuccess: (result: CreatedRoom | { index: number }) => void;
    onRoomProcessError: (index: number, error: any) => void;
    onAllSuccess: (result: { invoiceNumber: string }) => void;
    onError: (error: any) => void;
}

export const useCreateGroupBookingV4 = ({ onRoomProcessStart, onRoomProcessSuccess, onRoomProcessError, onAllSuccess, onError }: GroupBookingCallbacks) => {
    const [isPending, setIsPending] = useState(false);
    const queryClient = useQueryClient();

    const mutate = async (formData: CreateBookingFormV4, options: MutateOptions) => {
        setIsPending(true);

        const { createdRooms, dirtyFields } = options;
        let currentReservationId = createdRooms.length > 0 ? createdRooms[0].reservationId : null;
        // BARU: Variabel untuk menyimpan nomor invoice yang relevan
        let relevantInvoiceNumber: string | undefined;

        console.log('relevantInvoiceNumber:', relevantInvoiceNumber);

        const commonData = {
            property_id: formData.property_id,
            invoice_date: formData.invoice_date,
            public_note: formData.public_note,
            private_note: formData.private_note,
            customer_phone: formData.customer_phone,
            customer_address: formData.customer_address,
            customer_country: formData.customer_country,
            customer_state: formData.customer_state,
            customer_city: formData.customer_city,
            customer_zip: formData.customer_zip,
            customer_date_of_birth: formData.customer_date_of_birth,
            customer_identity_number: formData.customer_identity_number,
            agent_id: formData.agent_id === 'direct' ? null : formData.agent_id,
        };

        for (const [index, roomData] of formData.bookingroom.entries()) {
            const existingRoom = createdRooms.find(r => r.index === index);
            const isRoomDirty = dirtyFields.bookingroom && (dirtyFields.bookingroom as any)[index];
            // const isCommonDataDirty = dirtyFields.invoice_date || dirtyFields.status || dirtyFields.public_note || dirtyFields.customer_country;
            const isCommonDataDirty = dirtyFields.invoice_date ||
                dirtyFields.status ||
                dirtyFields.public_note ||
                dirtyFields.private_note || // Ditambahkan
                dirtyFields.customer_phone || // Ditambahkan
                dirtyFields.customer_address || // Ditambahkan
                dirtyFields.customer_country ||
                dirtyFields.customer_state || // Ditambahkan
                dirtyFields.customer_city || // Ditambahkan
                dirtyFields.customer_zip || // Ditambahkan
                dirtyFields.customer_date_of_birth || // Ditambahkan
                dirtyFields.customer_identity_number || // Ditambahkan
                dirtyFields.agent_id;

            // Panggil callback saat proses untuk kamar ini dimulai
            onRoomProcessStart(index);

            try {
                if (existingRoom) {
                    // SOLUSI: Ambil nomor invoice di awal blok, sebelum pengecekan 'dirty'.
                    // Ini memastikan 'relevantInvoiceNumber' selalu terisi untuk kamar yang ada.
                    relevantInvoiceNumber = roomData.invoice_number;

                    if (isRoomDirty || isCommonDataDirty) {
                        // Kirim field yang memengaruhi harga (rates, checkin_date, checkout_date,
                        // room_type_id, rate_plan_id, room_id) HANYA kalau salah satunya benar-benar
                        // dirty untuk baris kamar ini. Sebelumnya `rates` selalu ikut terkirim
                        // begitu ADA field apa pun yang dirty (termasuk public_note/private_note
                        // yang levelnya common data) -> BE selalu replace booking_rates, dan total
                        // amount bisa drift dari channex_amount walau user cuma edit nama/komentar.
                        //
                        // PENTING: kalau `shouldSendRates` false, SEMUA field di RATE_AFFECTING_FIELDS
                        // harus ikut dibuang dari payload, bukan cuma `rates`. Sebelumnya hanya
                        // `rates` yang dibuang sementara checkin_date/checkout_date tetap ikut
                        // terkirim (nilai lama, tidak berubah) -> backend (BookingController@update)
                        // melihat request.has('checkin_date') true lalu memvalidasi
                        // count(rates) === nights, padahal rates-nya sendiri tidak dikirim (0) ->
                        // selalu gagal dengan "Rates must match the number of nights." untuk
                        // perubahan field non-harga sekalipun, mis. ubah status jadi Cancelled.
                        const RATE_AFFECTING_FIELDS = ['rates', 'checkin_date', 'checkout_date', 'room_type_id', 'rate_plan_id', 'room_id'] as const;
                        const roomDirtyFields = isRoomDirty as Record<string, unknown> | true | undefined;
                        const shouldSendRates = roomDirtyFields === true
                            || RATE_AFFECTING_FIELDS.some((field) => !!roomDirtyFields?.[field]);

                        const roomPayload: Record<string, unknown> = { ...roomData };
                        if (!shouldSendRates) {
                            for (const field of RATE_AFFECTING_FIELDS) {
                                delete roomPayload[field];
                            }
                        }
                        const updatePayload = { ...commonData, ...roomPayload };
                        await privateApi.put(`/v4/booking/${existingRoom.bookingId}`, updatePayload);
                        onRoomProcessSuccess({ index });
                    } else {
                        onRoomProcessSuccess({ index }); // Anggap sukses (skipped)
                        continue;
                    }
                    // Ambil invoice number dari data yang ada
                    // relevantInvoiceNumber = roomData.invoice_number;
                } else {
                    const createPayload = { ...commonData, ...roomData, reservation_id: currentReservationId };
                    const response = await privateApi.post('/v4/booking', createPayload);
                    const newBooking = response.data?.data?.booking?.[0];

                    if (!newBooking || !newBooking.id || !newBooking.reservation_id) {
                        throw new Error(`Invalid API response for Room ${index + 1}.`);
                    }

                    const newlyCreatedRoom = { index, bookingId: newBooking.id, reservationId: newBooking.reservation_id };
                    onRoomProcessSuccess(newlyCreatedRoom);

                    currentReservationId = newBooking.reservation_id;
                    // Ambil invoice number dari respons API
                    relevantInvoiceNumber = newBooking.invoice_number;

                    // if (index === 0) {
                    //     currentReservationId = newBooking.reservation_id;
                    //     primaryInvoiceNumber = newBooking.invoice_number;
                    //     console.log('primaryInvoiceNumber updated:', primaryInvoiceNumber);
                    // }
                }
            } catch (error) {
                onRoomProcessError(index, error);
                setIsPending(false);
                onError(error);
                return; // Hentikan seluruh proses
            }
        }

        // const finalInvoiceNumber = primaryInvoiceNumber;
        // console.log('Final invoice number:', finalInvoiceNumber);

        console.log('relevantInvoiceNumber after processing:', relevantInvoiceNumber);

        if (!relevantInvoiceNumber) {
            toast.error("Could not determine invoice number to send.", { id: 'create-group-booking' });
            setIsPending(false);
            onError(new Error("Invoice number not found after processing."));
            return;
        }

        toast.success("All reservations processed successfully!", { id: 'create-group-booking' });
        await queryClient.invalidateQueries({ queryKey: ['bookings'] });
        // List reservasi aktif (useGetAllBookingV4) pakai key 'bookings-v4', bukan 'bookings'
        // saja — tanpa ini tabel /admin/reservation tidak auto-refresh setelah create/update.
        await queryClient.invalidateQueries({ queryKey: ['bookings-v4'] });
        setIsPending(false);
        onAllSuccess({ invoiceNumber: relevantInvoiceNumber });
    };

    return { mutate, isPending };
};